import type { ThresholdKind, ThresholdList, ThresholdSport } from '@painpal/contracts';
import { useMutationState, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { invalidateTraining, mutationKeys } from '../../api/keys';
import { useThresholds } from '../../api/queries';
import { useOnTransition } from '../../ui/useOnTransition';
import { recalcKindOf, recalcMessage, type RecalcKind } from './thresholdModel';
import styles from './settings.module.css';

export interface Recalculation { recalculating: ThresholdList['recalculating']; kind: RecalcKind }

// The server only says that a recalculation runs, not why, so the latest successful change decides the wording. A known
// limit: a recalculation the server starts by itself (an FTP auto-apply as an import ends, or a backdate) soon after a
// Max HR change in this tab is worded for HR zones, because that change is still the latest one.
function useRecalcKind(): RecalcKind {
  const latest = useMutationState({
    filters: { mutationKey: mutationKeys.thresholdChanges, status: 'success' },
    select: (mutation) => mutation.options.mutationKey,
  }).at(-1);
  if (latest === undefined) return 'ftp';
  return recalcKindOf(latest[1] as ThresholdSport, latest[2] as ThresholdKind);
}

// Once per page. recalculating is per user, so the FTP list alone drives it for the FTP and HR cards.
export function useRecalculation(): Recalculation {
  const queryClient = useQueryClient();
  const recalculating = useThresholds('ftp', 'ride').data?.recalculating ?? null;
  useOnTransition(recalculating, (_, next) => { if (next === null) void invalidateTraining(queryClient); });
  return { recalculating, kind: useRecalcKind() };
}

// The live region stays mounted and empty, because one that mounts with its text already in it often goes unannounced.
export function RecalcBanner({ recalculating, kind, slot }: Recalculation & { slot: RecalcKind }) {
  return (
    <div role="status" className={styles.recalcLive}>
      {recalculating !== null && kind === slot && (
        <p className={`alert-amber ${styles.recalc}`}><RefreshCw size={14} aria-hidden="true" />{recalcMessage(recalculating, kind)}</p>
      )}
    </div>
  );
}
